import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import ChatBox from "@/components/ChatBox";
import ShareRideButton from "@/components/ShareRideButton";
import { Calendar, MapPin, Mountain, Bike, Users, ExternalLink, ArrowLeft, Trash2, LogOut, Pencil } from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { toast } from "sonner";

const RIDE_TYPE_LABELS = {
  allenamento: "Allenamento", lento: "Lento", recupero: "Recupero",
  ripetute: "Ripetute", cicloturismo: "Cicloturismo", gara: "Gara", social: "Social",
};
const SURFACE_LABELS = { asfaltato: "Asfalto", ghiaino: "Ghiaino", sterrato: "Sterrato", rocce: "Rocce", misto: "Misto" };
const BIKE_LABELS = { bdc: "BDC", mb: "MB", gravel: "Gravel", citybike: "City", altro: "Altro" };
const LEVEL_STYLES = {
  tutti:        { label: "Tutti", color: "#A1A1AA" },
  principiante: { label: "Principiante",   color: "#10B981" },
  intermedio:    { label: "Intermedio",      color: "#3B82F6" },
  forte:         { label: "Forte",           color: "#F59E0B" },
  pro:           { label: "Pro",             color: "#E11D48" },
};

// Turn plain-text URLs (http://..., https://...) into clickable <a> tags.
function linkifyText(text) {
  if (!text) return null;
  const parts = String(text).split(/(https?:\/\/[^\s)]+)/g);
  return parts.map((p, i) => {
    if (/^https?:\/\//.test(p)) {
      return (
        <a
          key={i}
          href={p}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#E11D48] underline break-all hover:opacity-80"
        >
          {p}
        </a>
      );
    }
    return <span key={i}>{p}</span>;
  });
}

export default function RideDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [ride, setRide] = useState(null);
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/rides/${id}`);
      setRide(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      nav("/feed");
    }
  }, [id, nav]);

  useEffect(() => { load(); }, [load]);

  if (!ride) {
    return (
      <>
        <Navbar />
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="w-10 h-10 border-2 border-[#E11D48] border-t-transparent rounded-full animate-spin" />
        </div>
      </>
    );
  }

  const dt = new Date(ride.start_datetime);
  const isCreator = user && user.user_id === ride.creator_id;
  const isParticipant = user && (ride.participants || []).includes(user.user_id);

  const join = async () => {
    setActing(true);
    try { const { data } = await api.post(`/rides/${id}/join`); setRide(data); toast.success("Sei dentro!"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setActing(false); }
  };
  const leave = async () => {
    setActing(true);
    try { const { data } = await api.post(`/rides/${id}/leave`); setRide(data); toast.success("Hai abbandonato il giro"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setActing(false); }
  };
  const removeRide = async () => {
    if (!window.confirm("Eliminare il giro?")) return;
    try { await api.delete(`/rides/${id}`); toast.success("Giro eliminato"); nav("/feed"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <>
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <Link to="/feed" className="text-xs uppercase tracking-widest text-gray-500 hover:text-gray-900 inline-flex items-center gap-1 mb-6" data-testid="back-to-feed">
          <ArrowLeft className="w-3 h-3" /> Torna ai giri
        </Link>

        <div className="grid lg:grid-cols-[1fr_400px] gap-6">
          <div className="space-y-6 min-w-0">
            <div className="tactical-card rounded-sm p-6">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="label-tag mb-2">
                    {RIDE_TYPE_LABELS[ride.ride_type]} · {BIKE_LABELS[ride.bike_type]}
                  </div>
                  <h1 className="font-display font-black text-3xl md:text-4xl uppercase tracking-tighter text-gray-900 leading-tight">
                    {ride.title}
                  </h1>
                  <div className="flex items-center gap-2 mt-3 text-sm text-gray-700">
                    <span>by</span>
                    <span className="font-bold text-gray-900" data-testid="ride-creator">{ride.creator_name}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="label-tag text-[#E11D48]">{format(dt, "EEEE", { locale: it })}</div>
                  <div className="font-display font-black text-3xl">{format(dt, "d MMM", { locale: it })}</div>
                  <div className="font-mono text-xl">{format(dt, "HH:mm")}</div>
                  {(ride.flexibility_date_days > 0 || ride.flexibility_time_minutes > 0) && (
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 mt-1">
                      flex ±{ride.flexibility_date_days}g / {ride.flexibility_time_minutes}min
                    </div>
                  )}
                  {ride.updated_at && (
                    <div className="text-[10px] uppercase tracking-widest text-gray-400 mt-2" data-testid="ride-updated-at">
                      Aggiornato {format(new Date(ride.updated_at), "d MMM HH:mm", { locale: it })}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mt-6 py-5 border-y border-gray-200">
                <div>
                  <div className="label-tag">KM</div>
                  <div className="font-display font-black text-3xl">{ride.distance_km}</div>
                </div>
                <div>
                  <div className="label-tag">D+ m</div>
                  <div className="font-display font-black text-3xl">{ride.elevation_m}</div>
                </div>
                <div>
                  <div className="label-tag">Pace km/h</div>
                  <div className="font-display font-black text-3xl">{ride.pace_kmh || "—"}</div>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3 mt-5 text-sm">
                <div className="flex items-start gap-2 sm:col-span-2">
                  <MapPin className="w-4 h-4 text-[#E11D48] mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="label-tag mb-1">Partenza</div>
                    <div className="text-gray-700 font-medium" data-testid="ride-location-label">{ride.location?.label || ride.location?.name}</div>
                    {ride.location?.meeting_notes && (
                      <div className="mt-2" data-testid="ride-meeting-notes">
                        <div className="label-tag mb-0.5">Punto di ritrovo</div>
                        <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap break-words">
                          {linkifyText(ride.location.meeting_notes)}
                        </p>
                      </div>
                    )}
                    <div className="mt-2" data-testid="ride-when">
                      <div className="label-tag mb-0.5">Orario</div>
                      <p className="text-gray-700 text-sm leading-relaxed capitalize">
                        {format(new Date(ride.start_datetime), "EEEE d MMMM, HH:mm", { locale: it })}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#E11D48]" />
                  <span className="text-gray-700" data-testid="ride-participants-count">{ride.participants_count} partecipanti</span>
                </div>
                <div className="flex items-center gap-2 sm:col-span-2 flex-wrap">
                  {(() => {
                    const lvl = LEVEL_STYLES[ride.target_level] || LEVEL_STYLES.tutti;
                    return (ride.target_level && ride.target_level !== "tutti") && (
                      <span className="chip" style={{ background: `${lvl.color}1F`, color: lvl.color, borderColor: `${lvl.color}66` }}>
                        Livello: {lvl.label}
                      </span>
                    );
                  })()}
                  {ride.creator_power > 0 && (
                    <span className="chip" style={{ background: "rgba(255,59,48,0.12)", color: "#E11D48", borderColor: "rgba(255,59,48,0.4)" }}>
                      ⚡ Organizzatore {ride.creator_power}W
                    </span>
                  )}
                  {(ride.surface_types || []).map((s) => <span key={s} className="chip">{SURFACE_LABELS[s] || s}</span>)}
                </div>
              </div>

              {ride.description && (
                <div className="mt-5 pt-5 border-t border-gray-200">
                  <div className="label-tag mb-2">Descrizione</div>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{ride.description}</p>
                </div>
              )}

              {ride.komoot_link && (
                <a
                  href={ride.komoot_link} target="_blank" rel="noopener noreferrer"
                  data-testid="ride-komoot-link"
                  className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-[#6AA127] text-white font-bold uppercase tracking-wider text-xs rounded-sm hover:brightness-110"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Apri su Komoot
                </a>
              )}

              {ride.waypoints && ride.waypoints.length > 0 && (
                <div className="mt-5 pt-5 border-t border-gray-200">
                  <div className="label-tag mb-3">Tappe principali</div>
                  <div className="flex flex-wrap gap-2">
                    {ride.waypoints.map((w, idx) => {
                      const name = typeof w === "string" ? w : (w && w.name) || "";
                      if (!name) return null;
                      return (
                        <span key={`${name}-${idx}`} className="chip" style={{ background: "rgba(255,59,48,0.15)", color: "#E11D48", borderColor: "rgba(255,59,48,0.4)" }}>
                          <Mountain className="w-3 h-3" /> {name}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Action */}
              <div className="mt-6 flex flex-wrap gap-3">
                {!user && <Link to="/login" className="btn-primary px-6 py-3 rounded-sm" data-testid="ride-login-cta">Accedi per partecipare</Link>}
                {user && !isParticipant && (
                  <button onClick={join} disabled={acting} className="btn-primary px-6 py-3 rounded-sm disabled:opacity-50 flex items-center gap-2" data-testid="ride-join-btn">
                    <Bike className="w-4 h-4" /> Partecipa
                  </button>
                )}
                {user && isParticipant && !isCreator && (
                  <button onClick={leave} disabled={acting} className="px-6 py-3 border border-gray-300 hover:border-gray-900 text-gray-900 rounded-sm font-bold uppercase tracking-wider text-sm flex items-center gap-2" data-testid="ride-leave-btn">
                    <LogOut className="w-4 h-4" /> Abbandona
                  </button>
                )}
                {isCreator && (
                  <>
                    <button onClick={() => nav(`/ride/${id}/edit`)} className="px-6 py-3 border border-gray-300 hover:border-[#E11D48] hover:text-[#E11D48] text-gray-900 rounded-sm font-bold uppercase tracking-wider text-sm flex items-center gap-2 transition-colors" data-testid="ride-edit-btn">
                      <Pencil className="w-4 h-4" /> Modifica
                    </button>
                    <button onClick={removeRide} className="px-6 py-3 border border-red-700/40 text-red-400 hover:bg-red-900/30 rounded-sm font-bold uppercase tracking-wider text-sm flex items-center gap-2" data-testid="ride-delete-btn">
                      <Trash2 className="w-4 h-4" /> Elimina
                    </button>
                  </>
                )}
                <ShareRideButton ride={ride} />
              </div>
            </div>

            {ride.komoot_link && (
              <div className="tactical-card rounded-sm overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
                  <span className="label-tag">Tracciato Komoot</span>
                  <a href={ride.komoot_link} target="_blank" rel="noopener noreferrer" className="text-xs uppercase tracking-widest text-[#6AA127] hover:underline">Apri ↗</a>
                </div>
                <iframe
                  data-testid="komoot-embed"
                  src={ride.komoot_link.replace("/tour/", "/tour/").replace(/\/$/, "") + "/embed"}
                  title="Komoot tour"
                  width="100%"
                  height="360"
                  frameBorder="0"
                  scrolling="no"
                  style={{ background: "#171717" }}
                />
              </div>
            )}
          </div>

          <div className="lg:sticky lg:top-20 self-start space-y-4">
            {/* Participants list */}
            {(ride.participants_preview || []).length > 0 && (
              <div className="tactical-card rounded-sm p-5" data-testid="ride-participants-card">
                <div className="label-tag mb-3 flex items-center justify-between">
                  <span>Partecipanti</span>
                  <span className="text-gray-500 font-mono normal-case tracking-normal">{ride.participants_count}</span>
                </div>
                <div className="space-y-2">
                  {(ride.participants_preview || []).map((p) => (
                    <Link
                      key={p.user_id}
                      to={`/u/${p.user_id}`}
                      data-testid={`ride-participant-${p.user_id}`}
                      className="flex items-center gap-3 p-2 rounded-sm hover:bg-gray-50 transition-colors group"
                    >
                      <div className="w-9 h-9 rounded-full overflow-hidden bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0">
                        {p.picture ? (
                          <img src={p.picture} alt={p.first_name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-[11px] font-display font-black text-gray-500 uppercase tracking-tighter">
                            {(p.first_name || "?")[0]}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-gray-900 truncate group-hover:text-[#E11D48]">{p.first_name || "Ciclista"}</div>
                        {p.user_id === ride.creator_id && (
                          <div className="text-[10px] uppercase tracking-wider text-[#E11D48] font-bold">Organizzatore</div>
                        )}
                      </div>
                    </Link>
                  ))}
                  {ride.participants_count > (ride.participants_preview || []).length && (
                    <div className="text-xs text-gray-500 px-2 pt-2 border-t border-gray-100">
                      +{ride.participants_count - (ride.participants_preview || []).length} altri partecipanti
                    </div>
                  )}
                </div>
              </div>
            )}

            <ChatBox rideId={id} />
          </div>
        </div>
      </main>
    </>
  );
}
